import React, { useState, useEffect } from 'react';
import { B } from '../constants';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import DestinationCard from '../components/DestinationCard';
import { FullPhoto } from '../components/FallbackImg';
import { SkeletonCard } from '../components/ui/LoadingState';
import useDebouncedDestSearch from '../hooks/useDebouncedDestSearch';
import { destinations as destinationsAPI } from '../api';
import { trackActivity } from '../lib/activityEvents';
import Footer from '../components/Footer';

// Booking/Skyscanner-style grouping: broad geographic entities first.
const TYPE_GROUP_ORDER = ['City · Country', 'Country', 'City', 'Region'];
const TYPE_GROUP_LABELS = {
  'City · Country': 'Cities & Countries',
  Country: 'Countries',
  City: 'Cities',
  Region: 'Regions',
};

export default function GlobalSearchScreen({ goScreen }) {
  const [query, setQuery] = useState('');
  // Inline selection: picking a result shows its detail on THIS page instead of
  // navigating to the AI Planner or the full detail screen.
  const [selected, setSelected] = useState(null);
  const [fullDest, setFullDest] = useState(null);

  // Shared debounced + gated + cached destination search. Empty query returns
  // the default popular list; the city index is in memory, so 1-letter queries work.
  const { results: destinations, loading, error } = useDebouncedDestSearch(query, {
    surface: 'global',
    cacheScope: 'all',
    fetcher: (q) => destinationsAPI.search(q, { all: true }),
  });

  useEffect(() => {
    if (!selected) { setFullDest(null); return undefined; }
    let cancelled = false;
    destinationsAPI.get(selected.id)
      .then((r) => { if (!cancelled) setFullDest(r); })
      .catch(() => { if (!cancelled) setFullDest(selected); });
    return () => { cancelled = true; };
  }, [selected]);

  // Group results under type headers (only while actively searching — the
  // default popular list doesn't need Booking.com-style chrome).
  const groups = [];
  if (query) {
    TYPE_GROUP_ORDER.forEach(t => {
      const items = destinations.filter(d => (d.type || '') === t);
      if (items.length) groups.push([TYPE_GROUP_LABELS[t], items]);
    });
    const other = destinations.filter(d => !TYPE_GROUP_ORDER.includes(d.type));
    if (other.length) groups.push(['More places', other]);
    if (!groups.length && destinations.length) groups.push(['Results', destinations]);
  }

  const shown = fullDest || selected || {};

  return (
    <div style={{ background: B.sandPattern, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, background: 'white', zIndex: 100, borderBottom: '1px solid #e5e0d8' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px' }}>
          <button onClick={() => goScreen('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, padding: 4, color: B.night, flexShrink: 0 }}>←</button>
          <div style={{ flex: 1 }}>
            <Input
              placeholder="Search destinations everywhere..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              icon="🔍"
              size="lg"
              style={{ borderRadius: 12, border: '1px solid #e5e0d8', background: B.sand }}
              autoFocus
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div style={{ flex: 1, padding: '20px 20px 40px', maxWidth: 960, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 20 }}>
            {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: B.muted }}>{error}</p>
          </div>
        ) : destinations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
            <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: B.muted, marginBottom: 8 }}>
              {query ? `No destinations found for "${query}".` : 'Start typing to search destinations.'}
            </p>
            {query && (
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: '#999' }}>
                Try a different search term.
              </p>
            )}
          </div>
        ) : (
          <>
            {query && (
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: B.muted, marginBottom: 16 }}>
                {destinations.length} destination{destinations.length !== 1 ? 's' : ''} found
              </p>
            )}
            {(query ? groups : [['', destinations]]).map(([label, items]) => (
              <div key={label || 'all'}>
                {label && (
                  <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, color: B.muted, margin: '20px 0 12px' }}>
                    {label}
                  </h2>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 20 }}>
                  {items.map(dest => (
                    <DestinationCard
                      key={dest.id}
                      destination={dest}
                      onClick={() => {
                        trackActivity('search', 'search_result_clicked', { type: dest.type, id: dest.id, position: destinations.indexOf(dest) });
                        setSelected(dest);
                      }}
                      showPartners={false}
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Inline detail — same page, no navigation. */}
      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        size="xl"
        ariaLabel={`${shown.name || 'Destination'} details`}
        scrollBehavior="inside"
      >
        <div>
          <div style={{ position: 'relative', height: 260, borderRadius: 18, overflow: 'hidden' }}>
            {shown.img ? (
              <FullPhoto sources={[shown.img, ...(shown.imgFallbacks || [])]} alt={shown.name} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg,${B.forest},${B.mint})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56 }}>🌍</div>
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(15,25,35,0.7),transparent)' }} />
            {shown.tag && (
              <span style={{ position: 'absolute', top: 14, left: 14, background: 'rgba(255,255,255,0.93)', borderRadius: 50, padding: '5px 14px', fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>
                {shown.type || shown.tag}
              </span>
            )}
            <div style={{ position: 'absolute', bottom: 16, left: 20, right: 20 }}>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 700, color: 'white', textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>{shown.name}</h3>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginTop: 18 }}>
            <div style={{ background: 'white', border: '1px solid #e7e2d9', borderRadius: 14, padding: '12px 16px' }}>
              <b style={{ display: 'block', fontSize: 18, textTransform: 'capitalize' }}>{shown.tag || 'City'}</b>
              <span style={{ fontSize: 11, color: B.muted, fontWeight: 600, letterSpacing: 1 }}>TYPE</span>
            </div>
            <div style={{ background: 'white', border: '1px solid #e7e2d9', borderRadius: 14, padding: '12px 16px' }}>
              <b style={{ display: 'block', fontSize: 18 }}>{shown.country || '—'}</b>
              <span style={{ fontSize: 11, color: B.muted, fontWeight: 600, letterSpacing: 1 }}>COUNTRY</span>
            </div>
            <div style={{ background: 'white', border: '1px solid #e7e2d9', borderRadius: 14, padding: '12px 16px' }}>
              <b style={{ display: 'block', fontSize: 18 }}>{shown.placeCount ?? shown.plannablePlaces ?? '—'}</b>
              <span style={{ fontSize: 11, color: B.muted, fontWeight: 600, letterSpacing: 1 }}>{shown.placeCount != null ? 'PLACES' : 'PLANNABLE'}</span>
            </div>
          </div>

          {shown.description && (
            <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: B.muted, lineHeight: 1.7, marginTop: 16 }}>{shown.description}</p>
          )}
        </div>
      </Modal>

      <Footer goScreen={goScreen} />
    </div>
  );
}